import { useFormik } from "formik"
import React from "react"
import { Form, FormFeedback, Input, Label } from "reactstrap"
import * as Yup from "yup"
import ValueBuilder from "./ValueBuilder"
import { VAR_NAME_SUGGESTIONS } from "./varSuggestions"

const VAR_TYPES = [
  { value: "PATH", label: "Path" },
  { value: "LIT", label: "Literal" },
  { value: "FMT", label: "Format" },
  { value: "EXPR", label: "Expression" },
]

const formatInfoItem = item => {
  if (typeof item === "string") return item
  if (item.name && item.loadTypes?.length) {
    const lt = item.loadTypes.map(l => l.name).join(", ")
    return `${item.name} (${lt})`
  }
  return item.name || ""
}

const VariableForm = props => {
  const varData = props.varData || null

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: varData?.name || "",
      type: varData?.type || "PATH",
      value: varData?.value || "",
      notes: varData?.notes || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please enter name of variable"),
      value: varData?.required
        ? Yup.string().required("Value is required for this field")
        : Yup.string().nullable(),
    }),
    onSubmit: values => {
      props.onSave({
        name: values.name,
        label: varData?.label,
        type: values.type,
        value: values.value || "",
        notes: values.notes,
        required: varData?.required || false,
        information: varData?.information || [],
      })
    },
  })

  const { type, value } = validation.values
  const autoInfo = varData?.information?.length > 0

  return (
    <>
      <div className="modal-header">
        <h5 className="modal-title mt-0">
          {props.isNew ? "Add New Variable" : "Update Variable"}
        </h5>
      </div>
      <Form id="addVarForm" className="form-horizontal">
        <div className="modal-body">
          <div className="mb-3">
            <Label className="form-label" htmlFor="varname">
              Name <span className="text-danger">*</span>
            </Label>
            <Input
              id="varname"
              name="name"
              list="varNameSuggestions"
              autoComplete="off"
              placeholder="Enter variable name"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.name || ""}
              invalid={!!(validation.touched.name && validation.errors.name)}
            />
            <datalist id="varNameSuggestions">
              {VAR_NAME_SUGGESTIONS.map(name => (
                <option key={name} value={name} />
              ))}
            </datalist>
            {validation.touched.name && validation.errors.name && (
              <FormFeedback type="invalid">{validation.errors.name}</FormFeedback>
            )}
          </div>

          <div className="mb-3">
            <div className="opendock-source-pills" role="tablist" aria-label="Variable source">
              {VAR_TYPES.map(t => (
                <button
                  key={t.value}
                  type="button"
                  role="tab"
                  aria-selected={type === t.value}
                  className={`opendock-source-pill${type === t.value ? " is-active" : ""}`}
                  onClick={() => validation.setFieldValue("type", t.value)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-3">
            <Label className="form-label">
              Value {varData?.required && <span className="text-danger">*</span>}
            </Label>
            <ValueBuilder
              type={type}
              value={value}
              onChange={v => validation.setFieldValue("value", v)}
            />
            {validation.touched.value && validation.errors.value && (
              <div className="invalid-feedback d-block">{validation.errors.value}</div>
            )}
          </div>

          <div className="mb-3">
            <Label className="form-label" htmlFor="varNotes">Notes</Label>
            {autoInfo ? (
              <div className="opendock-notes-readonly">
                {varData.information.map((item, idx) => (
                  <div key={idx}>{formatInfoItem(item)}</div>
                ))}
              </div>
            ) : (
              <Input
                id="varNotes"
                name="notes"
                placeholder="Optional notes"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.notes || ""}
              />
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button
            id="addValButton"
            type="button"
            className="btn btn-primary"
            onClick={() => validation.handleSubmit()}
          >
            Save
          </button>{" "}
          <button type="button" className="btn btn-secondary" onClick={props.closeModal}>
            Cancel
          </button>
        </div>
      </Form>
    </>
  )
}

export default VariableForm
